import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  MenuProductOption,
  RestaurantMenuCategoryDTO,
  RestaurantMenuCategoryForm,
  RestaurantMenuCategoryPage,
  RestaurantMenuItemDTO,
  RestaurantMenuItemForm,
  RestaurantMenuItemPage,
} from '../../../types/restaurant/RestaurantMenuTypes';
import { RestaurantPreparationStationDTO } from '../../../types/restaurant/RestaurantPreparationStationTypes';

export const emptyMenuCategoryForm: RestaurantMenuCategoryForm = {
  code: '',
  name: '',
  description: '',
  icon: '',
  color: '#3C50E0',
  displayOrder: 1,
};
export const emptyMenuItemForm: RestaurantMenuItemForm = {
  productId: '',
  categoryId: '',
  displayName: '',
  description: '',
  preparationTimeMinutes: 15,
  preparationStationId: '',
  available: true,
  featured: false,
  displayOrder: 1,
};
interface State {
  categories: RestaurantMenuCategoryDTO[];
  categoryPage: RestaurantMenuCategoryPage | null;
  items: RestaurantMenuItemDTO[];
  itemPage: RestaurantMenuItemPage | null;
  products: MenuProductOption[];
  stations: RestaurantPreparationStationDTO[];
  categoryForm: RestaurantMenuCategoryForm;
  itemForm: RestaurantMenuItemForm;
  loading: boolean;
  updating: boolean;
  error: string;
  saving: boolean;
  categoryModalOpen: boolean;
  itemModalOpen: boolean;
}
const initialState: State = {
  categories: [],
  categoryPage: null,
  items: [],
  itemPage: null,
  products: [],
  stations: [],
  categoryForm: emptyMenuCategoryForm,
  itemForm: emptyMenuItemForm,
  loading: false,
  updating: false,
  error: '',
  saving: false,
  categoryModalOpen: false,
  itemModalOpen: false,
};
const slice = createSlice({
  name: 'restaurantMenu',
  initialState,
  reducers: {
    setMenuCategories: (s, a: PayloadAction<RestaurantMenuCategoryDTO[]>) => {
      s.categories = a.payload;
    },
    setMenuCategoryPage: (
      s,
      a: PayloadAction<RestaurantMenuCategoryPage | null>
    ) => {
      s.categoryPage = a.payload;
      s.categories = a.payload?.content ?? [];
    },
    setMenuItems: (s, a: PayloadAction<RestaurantMenuItemDTO[]>) => {
      s.items = a.payload;
    },
    setMenuItemPage: (s, a: PayloadAction<RestaurantMenuItemPage | null>) => {
      s.itemPage = a.payload;
      s.items = a.payload?.content ?? [];
    },
    setMenuProducts: (s, a: PayloadAction<MenuProductOption[]>) => {
      s.products = a.payload;
    },
    setMenuStations: (
      s,
      a: PayloadAction<RestaurantPreparationStationDTO[]>
    ) => {
      s.stations = a.payload;
    },
    setMenuLoading: (s, a: PayloadAction<boolean>) => {
      s.loading = a.payload;
    },
    setMenuUpdating: (s, a: PayloadAction<boolean>) => {
      s.updating = a.payload;
    },
    setMenuError: (s, a: PayloadAction<string>) => {
      s.error = a.payload;
    },
    resetMenuCategoryResults: (s) => {
      s.categories = [];
      s.categoryPage = null;
      s.loading = false;
      s.updating = false;
      s.error = '';
    },
    setMenuSaving: (s, a: PayloadAction<boolean>) => {
      s.saving = a.payload;
    },
    openMenuCategoryCreate: (s) => {
      s.categoryForm = { ...emptyMenuCategoryForm };
      s.categoryModalOpen = true;
    },
    openMenuCategoryEdit: (s, a: PayloadAction<RestaurantMenuCategoryDTO>) => {
      const x = a.payload;
      s.categoryForm = {
        id: x.id,
        code: x.code,
        name: x.name,
        description: x.description ?? '',
        icon: x.icon ?? '',
        color: x.color ?? '#3C50E0',
        displayOrder: x.displayOrder,
        active: x.active,
      };
      s.categoryModalOpen = true;
    },
    closeMenuCategory: (s) => {
      s.categoryModalOpen = false;
      s.categoryForm = { ...emptyMenuCategoryForm };
    },
    updateMenuCategoryField: (
      s,
      a: PayloadAction<{ field: keyof RestaurantMenuCategoryForm; value: any }>
    ) => {
      s.categoryForm = {
        ...s.categoryForm,
        [a.payload.field]: a.payload.value,
      };
    },
    openMenuItemCreate: (s) => {
      s.itemForm = { ...emptyMenuItemForm };
      s.itemModalOpen = true;
    },
    openMenuItemEdit: (s, a: PayloadAction<RestaurantMenuItemDTO>) => {
      const x = a.payload;
      s.itemForm = {
        id: x.id,
        productId: x.productId,
        productName: x.productName,
        productCode: x.productCode,
        categoryId: x.categoryId,
        code: x.code,
        displayName: x.displayName ?? '',
        description: x.description ?? '',
        preparationTimeMinutes: x.preparationTimeMinutes ?? '',
        preparationStationId: x.preparationStationId ?? '',
        available: x.available,
        featured: x.featured,
        displayOrder: x.displayOrder,
        active: x.active,
      };
      s.itemModalOpen = true;
    },
    closeMenuItem: (s) => {
      s.itemModalOpen = false;
      s.itemForm = { ...emptyMenuItemForm };
    },
    updateMenuItemField: (
      s,
      a: PayloadAction<{ field: keyof RestaurantMenuItemForm; value: any }>
    ) => {
      s.itemForm = { ...s.itemForm, [a.payload.field]: a.payload.value };
    },
  },
});
export const {
  setMenuCategories,
  setMenuCategoryPage,
  setMenuItems,
  setMenuItemPage,
  setMenuProducts,
  setMenuStations,
  setMenuLoading,
  setMenuUpdating,
  setMenuError,
  resetMenuCategoryResults,
  setMenuSaving,
  openMenuCategoryCreate,
  openMenuCategoryEdit,
  closeMenuCategory,
  updateMenuCategoryField,
  openMenuItemCreate,
  openMenuItemEdit,
  closeMenuItem,
  updateMenuItemField,
} = slice.actions;
export default slice.reducer;
